import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ComplianceAreaCard from "@/components/compliance/ComplianceAreaCard";
import ComplianceItemList from "@/components/compliance/ComplianceItemList";
import ComplianceItemForm from "@/components/compliance/ComplianceItemForm";
import { COMPLIANCE_TEMPLATES, AREA_CONFIG } from "@/components/compliance/ComplianceTemplates";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Clock,
  Sparkles, RotateCcw, ChevronLeft,
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

const AREAS = ["rgpd", "prl", "harassment", "appcc"];

export default function RegulatoryCompliance() {
  const { currentVenue, user, userRole, loading: venueLoading } = useVenue();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeArea, setActiveArea] = useState(null); // null = overview
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [generatingTemplates, setGeneratingTemplates] = useState(false);

  const isAdmin = userRole === "superadmin" || userRole === "admin";

  useEffect(() => {
    if (currentVenue) loadItems();
  }, [currentVenue]);

  const loadItems = async () => {
    setLoading(true);
    const data = await hosys.entities.ComplianceItem.filter({ venue_id: currentVenue.id }, "-created_date");
    setItems(data);
    setLoading(false);
  };

  const handleGenerateTemplates = async () => {
    const area = activeArea || "all";
    const areasToGenerate = area === "all" ? AREAS : [area];
    setGeneratingTemplates(true);
    const today = moment().format("YYYY-MM-DD");
    const existingTitles = items.map(i => i.title.toLowerCase());

    for (const areaKey of areasToGenerate) {
      const templates = COMPLIANCE_TEMPLATES[areaKey] || [];
      for (const tpl of templates) {
        if (!existingTitles.includes(tpl.title.toLowerCase())) {
          const nextReview = tpl.review_period_months > 0
            ? moment().add(tpl.review_period_months, "months").format("YYYY-MM-DD")
            : "";
          await hosys.entities.ComplianceItem.create({
            venue_id: currentVenue.id,
            area: areaKey,
            status: "pending",
            next_review_date: nextReview,
            ...tpl,
          });
        }
      }
    }
    setGeneratingTemplates(false);
    await loadItems();
  };

  if (venueLoading || loading) return <QuickLoader />;

  // Stats globals
  const totalCompliant = items.filter(i => i.status === "compliant").length;
  const totalPending = items.filter(i => i.status === "pending").length;
  const totalNonCompliant = items.filter(i => i.status === "non_compliant").length;
  const totalApplicable = items.filter(i => i.status !== "not_applicable").length;
  const globalPct = totalApplicable > 0 ? Math.round((totalCompliant / totalApplicable) * 100) : null;

  const overdueItems = items.filter(i =>
    i.due_date && moment(i.due_date).isBefore(moment(), "day") && i.status !== "compliant"
  );
  const needsReview = items.filter(i =>
    i.next_review_date && moment(i.next_review_date).isSameOrBefore(moment().add(7, "days"), "day")
  );

  const areaItems = (area) => items.filter(i => i.area === area);

  const pctColor = (pct) => pct === null ? "text-gray-400" : pct >= 80 ? "text-emerald-600" : pct >= 50 ? "text-amber-500" : "text-red-600";
  const pctBg = (pct) => pct === null ? "bg-gray-200" : pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-red-500";

  // View: area detail
  if (activeArea) {
    const areaConfig = AREA_CONFIG[activeArea];
    const areaItemsList = areaItems(activeArea);

    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setActiveArea(null)} className="gap-1 text-gray-500">
            <ChevronLeft className="w-4 h-4" /> Tornar
          </Button>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${areaConfig.bg}`}>
            {areaConfig.icon}
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">{areaConfig.label}</h1>
            <p className="text-sm text-gray-400">{areaConfig.description}</p>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateTemplates}
              disabled={generatingTemplates}
              className="gap-2 text-xs"
            >
              {generatingTemplates
                ? <><RotateCcw className="w-3 h-3 animate-spin" /> Generant...</>
                : <><Sparkles className="w-3 h-3" /> Generar plantilla</>
              }
            </Button>
          )}
        </div>

        {/* Area summary */}
        {areaItemsList.length > 0 && (() => {
          const comp = areaItemsList.filter(i => i.status === "compliant").length;
          const app = areaItemsList.filter(i => i.status !== "not_applicable").length;
          const pct = app > 0 ? Math.round((comp / app) * 100) : null;
          return (
            <Card className="p-4 border-0 shadow-sm bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center gap-4">
                <div>
                  <span className={`text-3xl font-bold ${pctColor(pct)}`}>{pct !== null ? `${pct}%` : "—"}</span>
                  <p className="text-xs text-gray-400 mt-0.5">Compliment</p>
                </div>
                <div className="flex-1">
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div className={`h-3 rounded-full ${pctBg(pct)}`} style={{ width: pct !== null ? `${pct}%` : "0%" }} />
                  </div>
                  <div className="flex gap-4 mt-1.5 text-xs text-gray-500">
                    <span className="text-emerald-600 font-medium">{comp} complerts</span>
                    <span className="text-amber-600 font-medium">{areaItemsList.filter(i => i.status === "pending").length} pendents</span>
                    <span className="text-red-600 font-medium">{areaItemsList.filter(i => i.status === "non_compliant").length} incomplerts</span>
                  </div>
                </div>
              </div>
            </Card>
          );
        })()}

        <ComplianceItemList
          items={areaItemsList}
          onEdit={(item) => { setEditingItem(item); setShowForm(true); }}
          onDeleted={(id) => setItems(prev => prev.filter(i => i.id !== id))}
          onAdd={() => { setEditingItem(null); setShowForm(true); }}
          isAdmin={isAdmin}
        />

        <ComplianceItemForm
          open={showForm}
          onClose={() => { setShowForm(false); setEditingItem(null); }}
          item={editingItem}
          venueId={currentVenue.id}
          defaultArea={activeArea}
          onSaved={loadItems}
        />
      </div>
    );
  }

  // View: Overview
  return (
    <div className="space-y-5">
      <PageHeader
        title="Compliment Normatiu"
        subtitle={`${currentVenue?.name} · Centre de gestió normativa`}
        actions={
          isAdmin && (
            <Button
              onClick={handleGenerateTemplates}
              disabled={generatingTemplates}
              variant="outline"
              className="gap-2"
            >
              {generatingTemplates
                ? <><RotateCcw className="w-4 h-4 animate-spin" /> Generant...</>
                : <><Sparkles className="w-4 h-4" /> Generar totes les plantilles</>
              }
            </Button>
          )
        }
      />

      {/* Global score */}
      <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-[#7B2D3F]/5 to-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full border-4 border-[#7B2D3F]/20 flex items-center justify-center flex-shrink-0 bg-white shadow-inner">
              <span className={`text-2xl font-bold ${pctColor(globalPct)}`}>
                {globalPct !== null ? `${globalPct}%` : "—"}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Índex de Compliment Global</h2>
              <p className="text-sm text-gray-500">{totalCompliant} complerts · {totalPending} pendents · {totalNonCompliant} incomplerts</p>
              {globalPct !== null && (
                <div className="mt-2 w-48 bg-gray-100 rounded-full h-2.5">
                  <div className={`h-2.5 rounded-full ${pctBg(globalPct)}`} style={{ width: `${globalPct}%` }} />
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 flex-wrap sm:ml-auto">
            {overdueItems.length > 0 && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <div>
                  <p className="text-sm font-bold text-red-700">{overdueItems.length}</p>
                  <p className="text-[10px] text-red-500">Vençudes</p>
                </div>
              </div>
            )}
            {needsReview.length > 0 && (
              <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-3 py-2">
                <Clock className="w-4 h-4 text-purple-500" />
                <div>
                  <p className="text-sm font-bold text-purple-700">{needsReview.length}</p>
                  <p className="text-[10px] text-purple-500">Per revisar</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <div>
                <p className="text-sm font-bold text-emerald-700">{totalCompliant}</p>
                <p className="text-[10px] text-emerald-500">Complerts</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Alerts */}
      {overdueItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <p className="font-semibold text-red-800 text-sm">{overdueItems.length} obligació(ns) amb data vençuda</p>
          </div>
          <div className="space-y-1">
            {overdueItems.slice(0, 3).map(item => (
              <div key={item.id} className="flex items-center gap-2 text-xs text-red-700">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${AREA_CONFIG[item.area]?.bg || "bg-red-200"}`} />
                <span className="font-medium">{AREA_CONFIG[item.area]?.shortLabel}</span>
                <span className="truncate">{item.title}</span>
                <span className="ml-auto text-red-500 font-medium shrink-0">Vencuda {moment(item.due_date).fromNow()}</span>
              </div>
            ))}
            {overdueItems.length > 3 && <p className="text-xs text-red-500 mt-1">...i {overdueItems.length - 3} més</p>}
          </div>
        </div>
      )}

      {/* Area cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {AREAS.map(area => (
          <ComplianceAreaCard
            key={area}
            area={AREA_CONFIG[area]}
            items={areaItems(area)}
            onClick={() => setActiveArea(area)}
          />
        ))}
      </div>

      {/* Empty state */}
      {items.length === 0 && (
        <Card className="p-8 border-0 shadow-sm text-center border-2 border-dashed border-gray-200">
          <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="font-semibold text-gray-700 mb-1">Comença el teu centre de compliment normatiu</p>
          <p className="text-sm text-gray-400 mb-5 max-w-sm mx-auto">
            Genera automàticament les obligacions estàndard per a restaurants (RGPD, PRL, Assetjament i APPCC) amb un clic.
          </p>
          {isAdmin && (
            <Button
              onClick={handleGenerateTemplates}
              disabled={generatingTemplates}
              className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
            >
              {generatingTemplates
                ? <><RotateCcw className="w-4 h-4 animate-spin" /> Generant plantilles...</>
                : <><Sparkles className="w-4 h-4" /> Generar plantilles estàndard</>
              }
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}